import api from './api';

/**
 * Mirrors the backend `NotificationView` DTO
 * (backend/src/services/notification.service.ts). The backend owns this wire
 * contract and aliases the raw columns (`notification_id` / `is_read`) to
 * `id` / `read` in SQL — this type consumes it verbatim (djin-cloud#739).
 *
 * `type` is intentionally `string`: alarms.notifications.type is free-form
 * VARCHAR(50) and gains new kinds without a migration. 'device_availability'
 * is already written today, and CAL-14 adds 'schedule_event'. NotificationItem's
 * ICON_MAP falls back to the Bell icon for anything it does not know.
 */
export interface Notification {
  id: string;
  type: string;
  severity: 'critical' | 'high' | 'medium' | 'low' | 'info' | string;
  title: string;
  message: string | null;
  metadata?: Record<string, unknown>;
  read: boolean;
  read_at?: string | null;
  created_at: string;
}

/** Envelope returned by GET /v1/notifications — `total` is the unpaginated count. */
export interface NotificationList {
  notifications: Notification[];
  total: number;
}

export async function fetchNotifications(): Promise<NotificationList> {
  const res = await api.get<{ success: boolean; data: NotificationList }>('/v1/notifications');
  return res.data.data;
}

export async function fetchUnreadCount(): Promise<number> {
  const res = await api.get<{ success: boolean; data: { unread: number } }>('/v1/notifications/count');
  return res.data.data.unread;
}

export async function markAsRead(id: string): Promise<void> {
  await api.put(`/v1/notifications/${encodeURIComponent(id)}/read`);
}

export async function markAllAsRead(): Promise<number> {
  const res = await api.put<{ success: boolean; data: { marked: number } }>('/v1/notifications/read-all');
  return res.data.data.marked;
}
